import { FC, useRef, useState } from "react";
import type { Market } from "@module-bindings/types";
import { PairIcon } from "@/components/TokenIcon";
import { formatMarketPairLabel } from "@/lib/marketUtils";
import { cn } from "@/lib/utils";
import { currentPrice as sourcePrice, priceChange24h } from "@/lib/mockData";
import { useMarketStats } from "./MarketStats";

interface MarketOverviewBarProps {
  selectedMarket: Market | null;
  markets: readonly Market[];
  onSelectMarket: (market: Market) => void;
  compact?: boolean;
}

export const MarketOverviewBar: FC<MarketOverviewBarProps> = ({
  selectedMarket,
  markets,
  onSelectMarket,
  compact = false,
}) => {
  const marketStats = useMarketStats(selectedMarket);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pairLabel = selectedMarket ? formatMarketPairLabel(selectedMarket) : "No Markets";
  const isPositive = priceChange24h >= 0;
  const displayPrice = sourcePrice > 0 ? (1 / sourcePrice).toFixed(5) : "0";

  return (
    <div
      className={cn(
        "sr-panel flex items-stretch overflow-visible",
        compact && "flex-col gap-1.5 px-2.5 py-1.5",
      )}
    >
      <div
        className={cn(
          "relative flex min-w-0 items-center gap-3 px-4 py-3",
          compact && "justify-between gap-2 px-0 py-0",
        )}
        ref={dropdownRef}
      >
        {selectedMarket ? (
          <PairIcon
            base={selectedMarket.tradingPair.base}
            quote={selectedMarket.tradingPair.quote}
            size={28}
          />
        ) : (
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-sr-pink-muted bg-sr-espresso">
            <span className="text-[10px] font-bold text-sr-neutral-500">?</span>
          </div>
        )}

        <div className={cn("flex min-w-0 items-center gap-2.5", compact && "min-w-0 flex-1 justify-between gap-2.5")}>
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={cn("truncate text-base font-medium text-white", compact && "text-[14px]")}>{pairLabel}</span>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              onBlur={(e) => {
                if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
                  setDropdownOpen(false);
                }
              }}
              className="sr-focus-ring rounded-md text-sr-neutral-300 transition-colors hover:text-white"
              aria-label="Select market"
              aria-expanded={dropdownOpen}
              aria-haspopup="listbox"
              disabled={markets.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className={cn("flex shrink-0 items-center gap-2.5", compact && "gap-1.5")}>
            <span className={cn("font-display text-xl font-medium tabular-nums text-white", compact && "text-[17px]")}>
              {displayPrice}
            </span>
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[11px] font-medium",
                compact && "px-1.5 py-0.5 text-[10px]",
                isPositive
                  ? "bg-sr-mint/12 text-sr-mint"
                  : "bg-sr-salmon/12 text-sr-salmon",
              )}
            >
              {isPositive ? "+" : ""}{priceChange24h}%
            </span>
          </div>
        </div>

        {dropdownOpen && markets.length > 0 && (
          <div
            className={cn(
              "absolute left-5 top-full z-50 mt-2 min-w-[180px] rounded-xl border border-sr-border-subtle bg-sr-surface p-1 shadow-xl",
              compact && "left-0 w-full min-w-0",
            )}
            role="listbox"
            aria-label="Markets"
          >
            {markets.map((market) => {
              const label = formatMarketPairLabel(market);
              const isActive = selectedMarket?.key === market.key;

              return (
                <button
                  key={market.key}
                  onClick={() => {
                    onSelectMarket(market);
                    setDropdownOpen(false);
                  }}
                  role="option"
                  aria-selected={isActive}
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-sr-lemon-muted/10 text-sr-lemon-muted"
                      : "text-sr-neutral-300 hover:bg-sr-neutral-900 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!compact && (
        <div className="flex min-w-0 flex-1 items-stretch">
          {marketStats.map((stat) => (
            <div
              key={stat.label}
              className="flex min-w-0 flex-1 flex-col justify-center px-4 py-3"
            >
              <span className="truncate text-[10px] font-medium leading-none text-sr-neutral-500">
                {stat.label}
              </span>
              <span className={`mt-2 block truncate font-display text-[15px] font-medium leading-none ${stat.placeholder ? "text-sr-neutral-700" : "text-white"}`}>
                {stat.value}
              </span>
              {stat.sub ? (
                <span className="mt-1 block truncate text-[10px] leading-none text-sr-neutral-400">
                  {stat.sub}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
